import type { OperatorInstance } from '@lyra/shared';
import { Section } from '@/components/ui/Section';
import { StatusLabel } from '@/components/ui/StatusLabel';
import { formatCurrencyEurRo, formatDateTimeRo, formatNumberRo } from '@/lib/format';
import { useOperatorInstances } from './api/operatorHooks';

const usdFormatter = new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function formatUsd(n: number): string {
  return `${usdFormatter.format(n)} $`;
}

/**
 * `/operator/organizatii`'s "Instanțe active" section (Task 8, step 3) — a
 * SMALL cross-org list of active Fleet rentals: rate, accrued cost, Fleet's
 * own reported spend, and a prominent flag on any `destroy_failed` row (a
 * session whose billing already stopped but whose underlying GPU instance
 * may still be running on the provider's side — see `OperatorInstance`'s
 * doc comment in `@lyra/shared` for why that's included here at all).
 * Deliberately minimal: no pagination/search (this list is meant to stay
 * short — it's "what's running right now", not a historical log).
 */
export function OperatorInstances() {
  const { data, isPending, isError } = useOperatorInstances();
  const items = data?.items ?? [];
  const destroyFailedCount = items.filter((i) => i.provisioningState === 'destroy_failed').length;

  return (
    <div className="mt-10">
      <Section title="Instanțe active">
        {isPending ? (
          <span className="text-[13.5px] text-ink-3">Se încarcă…</span>
        ) : (
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-[20px] font-[650] tabular-nums text-ink">{formatNumberRo(items.length)}</span>
            <span className="text-[14px] text-ink-2">instanțe Fleet active</span>
            {destroyFailedCount > 0 && (
              <span className="text-[13.5px] font-medium text-bad">
                · {formatNumberRo(destroyFailedCount)} necesită intervenție manuală
              </span>
            )}
          </div>
        )}

        <div className="mt-4">
          {isError && <p className="text-[13.5px] text-bad">Nu s-au putut încărca instanțele active.</p>}

          {!isError && !isPending && items.length === 0 && (
            <p className="text-[13.5px] text-ink-3">Nicio instanță Fleet activă.</p>
          )}

          {!isError && isPending && (
            <div className="h-16 animate-pulse rounded-xl bg-wash" aria-hidden />
          )}

          {!isError && !isPending && items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[780px] text-[13.5px]">
                <thead>
                  <tr className="border-b border-rule text-left text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-3 [&>th]:px-4 [&>th]:py-2.5">
                    <th>Organizație</th>
                    <th className="text-right">Rată</th>
                    <th className="text-right">Acumulat</th>
                    <th className="text-right">Cheltuit (Fleet)</th>
                    <th>Pornită</th>
                    <th>Stare</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item: OperatorInstance) => {
                    const flagged = item.provisioningState === 'destroy_failed';
                    return (
                      <tr
                        key={item.sessionId}
                        className={`border-b border-rule-soft align-top ${flagged ? 'bg-bad/10' : 'hover:bg-wash'}`}
                      >
                        <td className="px-4 py-2 text-ink">{item.organizationName}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-ink-2">{formatCurrencyEurRo(item.hourlyPriceEur)}/oră</td>
                        <td className="px-4 py-2 text-right tabular-nums text-ink-2">{formatCurrencyEurRo(item.accruedEur)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-ink-2">
                          {item.totalSpentUsd != null ? formatUsd(item.totalSpentUsd) : '—'}
                        </td>
                        <td className="px-4 py-2 text-ink-2">{formatDateTimeRo(item.startedAt)}</td>
                        <td className="px-4 py-2">
                          {flagged ? (
                            // Not a `StatusLabel`: this is an instruction sentence
                            // ("go check Fleet"), not a status word — the leading
                            // dot+label idiom is for compact status tokens like
                            // "Activă"/"Se pornește" below. The row is already
                            // highlighted (`bg-bad/10` above) and the text itself
                            // states the meaning outright, so plain bold red text
                            // (matching this section's own "necesită intervenție
                            // manuală" summary line above) reads better than
                            // cramming a full sentence behind a decorative dot.
                            <span className="text-[13.5px] font-medium text-bad">
                              Distrugere eșuată — verificați manual pe Fleet
                            </span>
                          ) : item.provisioningState === 'provisioning' ? (
                            <StatusLabel tone="warn" label="Se pornește" />
                          ) : (
                            <StatusLabel tone="teal" label="Activă" pulse />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
